import { axiosFetch as authFetch } from './axiosFetch';
import { parseResponse } from './crypto';
import { CREDENTIALS } from '../constants';
import { reportApiTracker } from '../utils/reportApiTracker';
import {
    dateStrInZone,
    shiftDateStr,
    getStoreTimeZone,
} from '../utils/dateTimeUtils';
import { resolveTokenRange, normalizeToken } from '../utils/reportPeriod';

// Match maghil-portal: use same reports base (REACT_APP_API_ENDPOINT_REPORTS)
const REPORTS_BASE_URL = process.env.REACT_APP_API_ENDPOINT_REPORTS || '';
// Owner View: Sales Overview by Days now lives on the Voice Agent backend, not the reports domain.
const VOICE_AGENT_BASE_URL = process.env.REACT_APP_API_ENDPOINT_VOICE_AGENT || '';

// Resolve token: use passed token or fallback to CREDENTIALS in localStorage (same key as maghil-portal).
// Portal: Store.getState()?.auth?.credentials?.accessToken and stores result.metaDataInfo.data under CREDENTIALS.
export function getReportToken(passedToken?: string | null): string | null {
    if (passedToken && typeof passedToken === 'string' && passedToken.trim()) return passedToken.trim();
    try {
        const raw = localStorage.getItem(CREDENTIALS);
        if (!raw) return null;
        const stored = JSON.parse(raw);
        const cred = stored?.credentials ?? stored;
        const t = cred?.accessToken ?? cred?.access_token ?? cred?.token ?? cred?.idToken ?? cred?.id_token ?? cred?.jwtToken ?? cred?.jwt;
        return t && typeof t === 'string' && t.trim() ? t.trim() : null;
    } catch {
        return null;
    }
}

export const fetchReport = async (url: string, token?: string | null, authScheme: string = 'bearer'): Promise<any> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'application/json' };
    const authToken = getReportToken(token);
    if (authToken) {
        // Match maghil-portal newReportsApi.js exactly: Authorization: 'bearer ' + token (lowercase).
        // authScheme lets a specific endpoint override this (e.g. getSalesOverviewByDays needs 'Bearer').
        headers['Authorization'] = `${authScheme} ${authToken}`;
    }

    reportApiTracker.trackStart();
    try {
        const response = await fetch(url, { headers });

        if (!response.ok) {
            const errText = await response.text().catch(() => '');
            console.error('[reportsApi] ERROR =>', { url, status: response.status, errText: errText?.substring(0, 200) });
            let errMsg = 'Failed to fetch report data';
            try { errMsg = JSON.parse(errText)?.message || errMsg; } catch { /* ignore */ }
            if (response.status === 403) errMsg = errMsg || 'Access denied. Please sign in again.';
            reportApiTracker.trackFailure();
            throw new Error(errMsg);
        }

        const text = await response.text();
        const decrypted = parseResponse(text);
        reportApiTracker.trackSuccess();
        return decrypted;
    } catch (err) {
        // Network-level failures (offline, DNS, abort) land here before trackFailure/trackSuccess run.
        // trackFailure is idempotent per request: only count if not already counted above.
        const s = reportApiTracker.getState();
        if (s.inflight > 0) reportApiTracker.trackFailure();
        throw err;
    }
};

// 1. Sales Summary
// GET /sales/summary?locationId=&startDate=&endDate=
export const getSalesSummary = ({ locationId, startDate, endDate, token }: any) => {
    const params = new URLSearchParams({ locationId, startDate, endDate });
    return fetchReport(`${REPORTS_BASE_URL}/sales/summary?${params}`, token);
};

// 2. Trends â€“ Sales by Duration
// GET /trends/sales/by-duration?locationId=&startDate=&endDate=&type=day&compareStartDate=&compareEndDate=
export const getSalesTrends = ({ locationId, startDate, endDate, type = 'day', compareStartDate, compareEndDate, token }: any) => {
    const params = new URLSearchParams({ locationId, startDate, endDate, type });
    if (compareStartDate) params.append('compareStartDate', compareStartDate);
    if (compareEndDate) params.append('compareEndDate', compareEndDate);
    return fetchReport(`${REPORTS_BASE_URL}/trends/sales/by-duration?${params}`, token);
};

// 3. Category Dropdown Details — v2 endpoint (path only; response is still [{ categoryId, categoryName }])
// GET /product/insights/v2/category-dropdown-details?locationId=&startDate=&endDate=
export const getCategoryDropdown = ({ locationId, startDate, endDate, token }: any) => {
    const params = new URLSearchParams({ locationId });
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    return fetchReport(`${REPORTS_BASE_URL}/product/insights/v2/category-dropdown-details?${params}`, token);
};

// 4. Category Channel Summary — v2 endpoint (path only; params/response shape unchanged)
// GET /product/insights/v2/getCategoryChannelSummary?locationId=&startDate=&endDate=&page=0&size=20&groupByCategory=true
export const getCategoryChannelSummary = ({ locationId, startDate, endDate, page = 0, size = 20, groupByCategory = true, orderSource, token }: any) => {
    const params = new URLSearchParams({ locationId, page, size, groupByCategory });
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (orderSource) params.set('orderSource', orderSource);
    return fetchReport(`${REPORTS_BASE_URL}/product/insights/v2/getCategoryChannelSummary?${params}`, token);
};

// 5. Category Sales
// GET /sales/category/getCategorySales?locationId=&startDate=&endDate=&page=0&size=20&groupByCategory=true
export const getCategorySales = ({ locationId, startDate, endDate, page = 0, size = 20, groupByCategory = true, token }: any) => {
    const params = new URLSearchParams({ locationId, startDate, endDate, page, size, groupByCategory });
    return fetchReport(`${REPORTS_BASE_URL}/sales/category/getCategorySales?${params}`, token);
};

// 6. Voided Details Summary
// GET /sales/category/getVoidedDetailsSummary?locationId=&startDate=&endDate=&groupByCategory=true
export const getVoidedDetailsSummary = ({ locationId, startDate, endDate, groupByCategory = true, token }: any) => {
    const params = new URLSearchParams({ locationId, startDate, endDate, groupByCategory });
    return fetchReport(`${REPORTS_BASE_URL}/sales/category/getVoidedDetailsSummary?${params}`, token);
};

// --- Build query string from payload (for report APIs that use locationId, startDate, endDate, page, size, etc.) ---
const buildQuery = (payload: any): string => {
    const p = payload || {};
    const params = new URLSearchParams();
    const loc = p.locationId ?? p.locationid;
    if (loc) params.set('locationId', loc);
    if (p.startDate) params.set('startDate', p.startDate);
    if (p.endDate) params.set('endDate', p.endDate);
    if (p.page != null) params.set('page', p.page);
    if (p.size != null) params.set('size', p.size);
    if (p.search != null && p.search !== undefined) params.set('search', p.search);
    if (p.searchQuery != null) params.set('search', p.searchQuery);
    if (p.tablePageNo != null) params.set('page', p.tablePageNo);
    if (p.tableRecordLimit != null) params.set('size', p.tableRecordLimit);
    if (p.groupByCategory != null) params.set('groupByCategory', p.groupByCategory);
    if (p.type) params.set('type', p.type);
    if (p.loyaltyCustomerId) params.set('loyaltyCustomerId', p.loyaltyCustomerId);
    if (p.dollarValuePerPoint != null) params.set('dollarValuePerPoint', String(p.dollarValuePerPoint));
    if (p.eventSlug !== undefined) params.set('eventSlug', p.eventSlug);
    if (p.eventDate) params.set('eventDate', p.eventDate);
    if (p.pickUpDate) params.set('pickUpDate', p.pickUpDate);
    if (p.startTime != null && p.startTime !== '') params.set('startTime', p.startTime);
    if (p.endTime != null && p.endTime !== '') params.set('endTime', p.endTime);
    if (p.sortBy != null) params.set('sortBy', p.sortBy);
    if (p.sortAsc != null) params.set('sortAsc', p.sortAsc);
    if (p.sortOrder != null) params.set('sortOrder', p.sortOrder);
    if (p.customerName !== undefined) params.set('customerName', p.customerName);
    if (p.phoneNumber != null && p.phoneNumber !== '') params.set('phoneNumber', '%2B' + String(p.phoneNumber).replace(/^\+/, ''));
    if (p.paymentType != null && p.paymentType !== '') params.set('paymentType', p.paymentType);
    if (p.filter != null && p.filter !== '') params.set('filter', p.filter);
    if (p.staffId != null && p.staffId !== '') params.set('staffId', p.staffId);
    if (p.sortOrder !== undefined && p.sortOrder !== null) params.set('sortOrder', String(p.sortOrder));
    if (p.isToday != null) params.set('isToday', String(p.isToday));
    if (p.paid != null) params.set('paid', String(p.paid));
    if (p.reason != null && p.reason !== '') params.set('reason', p.reason);
    if (p.considerThirdPartyValues != null) params.set('considerThirdPartyValues', p.considerThirdPartyValues);
    // Global sales-channel filter (Overall/Direct/3rd party) — see reports-channel-filter skill.
    if (p.orderSource) params.set('orderSource', p.orderSource);
    const qs = params.toString();
    // Append categoryId without URL-encoding the comma separators so multi-select
    // values go through as `id1,id2,id3` (not `id1%2C+id2`). UUIDs are URL-safe.
    if (p.categoryId != null && p.categoryId !== '') {
        const cat = `categoryId=${p.categoryId}`;
        return qs ? `${qs}&${cat}` : cat;
    }
    return qs;
};

const reportGet = (path: string, payload: any, tokenParam?: string | null): Promise<any> => {
    const q = buildQuery(payload);
    // Use explicit token param first, then payload.token, then getReportToken() will fall back to localStorage
    const token = tokenParam ?? payload?.token;
    return fetchReport(`${REPORTS_BASE_URL}${path}?${q}`, token);
};

// --- Sales Today's report: Dine-in, Non Dine-in, Voided, Complimentary (same as maghil-portal newReportsApi) ---
export const getLiveOrders = (opts: any) => reportGet('/sales/live/tables', opts, opts?.token);
export const getLiveOrderNonDineIn = (opts: any) => reportGet('/sales/live/tracking', opts, opts?.token);
export const getUnpaidVoidedOrders = (opts: any) => reportGet('/sales/live/unpaid-void-orders', opts, opts?.token);
export const getUnpaidComplementaryOrders = (opts: any) => reportGet('/sales/live/unpaid-comp-orders', opts, opts?.token);
export const getPaidComplementaryOrders = (opts: any) =>
    reportGet('/sales/live/paid-comp-orders', opts, opts?.token).catch(() => ({ content: [], data: [] }));
// Same as maghil-portal newReportsApi.js getPaidVoidedOrders: GET /sales/live/paid-void-orders?locationId=&page=&size=
// Portal uses REPORTS_API (base REACT_APP_API_ENDPOINT_REPORTS) + Authorization: 'bearer ' + token from auth.credentials.accessToken.
export const getPaidVoidedOrders = (opts: any) =>
    reportGet('/sales/live/paid-void-orders', opts, opts?.token).catch(() => ({ content: [], data: [] }));

// --- Snapshot / Today's report APIs (same as maghil-portal newReportsApi) ---
export const getSalesByChannel = (opts: any) => reportGet('/sales/channel', opts, opts?.token);
export const getDiscountSummary = (opts: any) => reportGet('/sales/discountSummary', opts, opts?.token);
// Backend may 500 if reason is null (NPE on reason.split); frontend tolerates empty response
export const getCancellationSummary = (opts: any) =>
    reportGet('/sales/cancelSummary', opts, opts?.token).catch(() => ({ content: [], data: [] }));
// GET /sales/live/canceledOrders â€” same URL as maghil-portal; token must be sent to avoid 403
export const getPaidCancelledOrders = (opts: any) => reportGet('/sales/live/canceledOrders', opts, opts?.token);
export const getEmployeeStaffActivity = (opts: any) => reportGet('/sales/employee/staffActivity', opts, opts?.token);
export const getEmployeeSalesOverview = (opts: any) => reportGet('/sales/employee/overview', opts, opts?.token);
export const getStaffTipsPerformance = (opts: any) => {
    const { locationId, startDate, endDate, token } = opts || {};
    const params = new URLSearchParams({ locationId, startDate, endDate, revenue: 'Tips' });
    return fetchReport(`${REPORTS_BASE_URL}/staff/overview/revenue-performance?${params}`, token).catch(() => []);
};
// By-duration (trends/sales/by-duration): use for hourly/period charts. Params: locationId, startDate, endDate, type=day, compareStartDate, compareEndDate.
export const getByDuration = (opts: any) => {
    const { locationId, startDate, endDate, type = 'day', compareStartDate, compareEndDate, token } = opts || {};
    const params = new URLSearchParams({ locationId, startDate, endDate, type });
    if (compareStartDate) params.append('compareStartDate', compareStartDate);
    if (compareEndDate) params.append('compareEndDate', compareEndDate);
    const url = `${REPORTS_BASE_URL}/trends/sales/by-duration?${params}`;
    console.log('[reportsApi] getByDuration request:', { url, locationId, startDate, endDate, type, compareStartDate, compareEndDate });
    return fetchReport(url, token).then((res) => {
        console.log('[reportsApi] getByDuration response:', JSON.stringify(res, null, 2));
        return res;
    }).catch(() => ({ current: [], compare: [] }));
};

// Legacy hourly endpoint (prefer getByDuration for new code)
export const getHourlySales = (opts: any) =>
    reportGet('/sales/hourly', opts, opts?.token).catch(() => ({ content: [] }));
export const getLiveNetSales = (opts: any) => reportGet('/sales/live/net-sales', opts, opts?.token);
// GET /sales/payment â€” historical tender/payment breakdown (locationId, startDate, endDate, page, size, sort)
export const getPaymentDetails = (opts: any) => reportGet('/sales/payment', opts, opts?.token);
// GET /sales/live/payment â€” today's tender mix (live)
export const getTenderTypes = (opts: any) => reportGet('/sales/live/payment', opts, opts?.token);
// GET /sales/live/refunds â€” live refunds for today (locationId only)
export const getLiveRefunds = (opts: any) => reportGet('/sales/live/refunds', opts, opts?.token);
export const getDropDownDetails = (opts: any) => reportGet('/sales/category/getDropDownDetails', opts, opts?.token);
export const getDailyCheckInStatus = (opts: any) => reportGet('/sales/checkIn/dailyCheckInStatus', opts, opts?.token);
export const getDailyGuest = (opts: any) => reportGet('/sales/checkIn/dailyGuests', opts, opts?.token);
export const getPeakSummary = (opts: any) => reportGet('/sales/checkIn/peakSummary', opts, opts?.token);
export const getVoidedOrderSummary = (opts: any) => reportGet('/sales/voidedSummary', opts, opts?.token);
export const getVoidedOrderPieChart = (opts: any) => reportGet('/sales/voided-order-pie-chart', opts, opts?.token);
// GET /sales/void-and-comp-summary â€” individual voided order rows (historical)
export const getVoidAndCompSummary = (opts: any) => reportGet('/sales/void-and-comp-summary', opts, opts?.token);
// GET /sales/live-voided-by-category â€” live voided items grouped by category (today)
export const getLiveVoidedByCategory = (opts: any) => reportGet('/sales/live-voided-by-category', opts, opts?.token);
// GET /sales/employee/staffTipAndGratuity â€” tips by staff
export const getEmployeeStaffTipGratuity = (opts: any) => {
    const q = buildQuery(opts);
    const url = `${REPORTS_BASE_URL}/sales/employee/staffTipAndGratuity?${q}`;
    console.log('[reportsApi] getEmployeeStaffTipGratuity request:', {
        url,
        locationId: opts?.locationId,
        startDate: opts?.startDate,
        endDate: opts?.endDate,
        tablePageNo: opts?.tablePageNo,
        tableRecordLimit: opts?.tableRecordLimit,
    });
    return fetchReport(url, opts?.token).then((res) => {
        console.log('[reportsApi] getEmployeeStaffTipGratuity response:', JSON.stringify(res, null, 2));
        return res;
    });
};
// GET /sales/staff â€” staff sales list
export const getStaffSales = (opts: any) => reportGet('/sales/staff', opts, opts?.token);
// GET /product/insights/popular-revenue?locationId=&startDate=&endDate=&filter=&sortOrder=
export const getPopularRevenue = (opts: any) => reportGet('/product/insights/popular-revenue', opts, opts?.token);
// Top/Bottom items by revenue — v2 endpoint (sortOrder=false/true). `filter` is lowercased
// here only (v2 expects "overall") without mutating the shared opts object other calls in
// the same Promise.all still rely on.
export const getPopularRevenueTop = (opts: any) => reportGet('/product/insights/v2/popular-revenue', { ...opts, filter: String(opts?.filter ?? 'overall').toLowerCase(), sortOrder: false }, opts?.token);
export const getPopularRevenueBottom = (opts: any) => reportGet('/product/insights/v2/popular-revenue', { ...opts, filter: String(opts?.filter ?? 'overall').toLowerCase(), sortOrder: true }, opts?.token);
// GET /product/insights/top-revenue-categories — category revenue (for donut)
export const getProductTopRevenueCategories = (opts: any) => reportGet('/product/insights/top-revenue-categories', opts, opts?.token).catch(() => []);
// GET /product/insights/cancelled-items â€” refunded items (itemName, itemCount)
export const getProductCancelledItems = (opts: any) => reportGet('/product/insights/cancelled-items', opts, opts?.token).catch(() => ({ content: [], data: [] }));
// GET /product/insights/items-cancelled-reason â€” refund reasons by item
export const getProductItemsCancelledReasons = (opts: any) => reportGet('/pproduct/insights/items-cancelled-reason', opts, opts?.token).catch(() => ({ content: [], data: [] }));
// GET /product/insights/v2/category-item-sales — full item table (v2; params/response shape unchanged —
// response now shaped as { items, currentPage, pageSize, totalItems, totalPages }, already handled by
// getCategoryItemSalesRows/mapCategoryItemSalesRow/readTableTotalItems/readTableTotalPages in the screen)
export const getProductCategoryItemSales = (opts: any) => reportGet('/product/insights/v2/category-item-sales', opts, opts?.token).catch(() => ({ content: [], data: [], totalItems: 0 }));
// GET /sales/live/v2/categories?locationId= — flat category list for dropdown
export const getLiveCategoriesV2 = (opts: any) => reportGet('/sales/live/v2/categories', opts, opts?.token).catch(() => []);
// GET /product/insights/v2/modifier-groups — modifier group list for the "All groups" filter dropdown
export const getModifierGroups = ({ locationId, token }: any) => {
    const params = new URLSearchParams({ locationId });
    return fetchReport(`${REPORTS_BASE_URL}/product/insights/v2/modifier-groups?${params}`, token).catch(() => []);
};
// GET /product/insights/v2/items — item list for the "All items" filter dropdown
export const getModifierItems = ({ locationId, token }: any) => {
    const params = new URLSearchParams({ locationId });
    return fetchReport(`${REPORTS_BASE_URL}/product/insights/v2/items?${params}`, token).catch(() => []);
};
// GET /product/insights/v2/modifier-analysis — Modifier Performance table (grouped/individual)
export const getModifierAnalysis = ({ locationId, startDate, endDate, modifierId, itemId, search, view = 'grouped', showUnpriced = false, page = 1, size = 10, sortBy = 'attachRate', sortDir = 'desc', orderSource, token }: any) => {
    const params = new URLSearchParams({ locationId, view, showUnpriced: String(showUnpriced), page: String(page), size: String(size), sortBy, sortDir });
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (modifierId) params.set('modifierId', modifierId);
    if (itemId) params.set('itemId', itemId);
    if (search) params.set('search', search);
    if (orderSource) params.set('orderSource', orderSource);
    // No .catch() fallback here — a swallowed error looks identical to a real "no
    // data" response and hides real failures (bad params, decrypt errors, 4xx/5xx)
    // behind an empty-state UI instead of the retry/error UI. Let it throw; the hook
    // that calls this is responsible for catching and surfacing SectionError.
    return fetchReport(`${REPORTS_BASE_URL}/product/insights/v2/modifier-analysis?${params}`, token);
};
// GET /sales/live/v2/item-sales — server-side paginated item list with category, search, and pinned support
export const getLiveItemSalesV2 = ({
    locationId,
    page = 0,
    size = 10,
    startDate,
    endDate,
    categoryIds,
    searchItem,
    pinnedItemNames,
    sortBy,
    sortAsc,
    orderSource,
    token,
}: {
    locationId: string;
    page?: number;
    size?: number;
    startDate?: string;
    endDate?: string;
    categoryIds?: string[];
    searchItem?: string;
    pinnedItemNames?: string[];
    sortBy?: string;
    sortAsc?: boolean;
    orderSource?: 'direct' | 'thirdparty';
    token?: string | null;
}): Promise<any> => {
    const params = new URLSearchParams({ locationId, page: String(page), size: String(size) });
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (categoryIds?.length) categoryIds.forEach((id) => params.append('categoryIds', id));
    if (searchItem) params.set('searchItem', searchItem);
    if (pinnedItemNames?.length) pinnedItemNames.forEach((n) => params.append('pinnedItemNames', n));
    if (sortBy) params.set('sortBy', sortBy);
    if (sortBy && sortAsc !== undefined) params.set('sortAsc', String(sortAsc));
    if (orderSource) params.set('orderSource', orderSource);
    return fetchReport(`${REPORTS_BASE_URL}/sales/live/v2/item-sales?${params}`, token)
        .catch(() => ({ content: [], pinnedItems: [], totalElements: 0, totalPages: 0 }));
};
// GET /product/insights/v2/tags — revenue by class (revenueClass, itemsSold, totalSales, sharePercent, avgPerItem)
export const getSalesByRevenueClass = (opts: any) => reportGet('/product/insights/v2/tags', opts, opts?.token).catch(() => ({ content: [], data: [] }));
export const getSnapshotSummary = (opts: any) => reportGet('/sales/snapshot-summary', opts, opts?.token).catch(() => ({}));
// GET /product/insights/v2/totals-revenue — Product report stamp KPIs (totalItemRevenue, totalItemsSold, averageRevenuePerItem)
export const getProductTotalsRevenue = (opts: any) => reportGet('/product/insights/v2/totals-revenue', opts, opts?.token).catch(() => ({}));

// GET /sales/live/category-item-sales?locationId=&startDate=&endDate=&page=&size=&categoryId=
// Item rows use grossSales (price) and salesQty (qty)
export const getCategoryItemSales = (opts: any) =>
    reportGet('/sales/live/category-item-sales', opts, opts?.token).catch(() => ({ content: [], data: [] }));
// GET /sales/employee/staff-clockings?locationId=&startDate=&endDate=&page=&size=
export const getStaffClockings = (opts: any) =>
    reportGet('/sales/employee/staff-clockings', opts, opts?.token).catch(() => ({ content: [], data: [] }));

// --- Live / Today-only APIs (no startDate/endDate â€” locationId only) ---
// GET /sales/live/billedOrders?locationId=&type=completed
export const getLiveBilledOrders = ({ locationId, type = 'completed', token }: any) => {
    const params = new URLSearchParams({ locationId, type });
    return fetchReport(`${REPORTS_BASE_URL}/sales/live/billedOrders?${params}`, token);
};
// GET /sales/live/item-sales?locationId=&page=0&size=10&search=&paid=false
export const getLiveItemSales = ({ locationId, page = 0, size = 10, search = '', sortBy = '', sortAsc = true, paid = false, token }: any) => {
    const params = new URLSearchParams({ locationId, page: String(page), size: String(size), search, sortBy, sortAsc: String(sortAsc), paid: String(paid) });
    return fetchReport(`${REPORTS_BASE_URL}/sales/live/item-sales?${params}`, token);
};

// --- EOD (same as maghil-portal eodReportsApi) ---
export const getEodReports = (opts: any) => reportGet('/eod/getEODReports', opts, opts?.token);
export const getEodTaxesData = (opts: any) => reportGet('/sales/live/v2/getTaxesData', opts, opts?.token);
export const getEodTaxDiscrepancy = (opts: any) => reportGet('/eod/getTaxDiscrepancy', opts, opts?.token);

// --- Check-in (same as maghil-portal checkInReportsApi) ---
// Live Check-In (no startDate/endDate; locationId only, plus page/size/search/type for tables)
export const getLiveCheckInOverview = (opts: any) => reportGet('/live/checkin/checkin-overview', opts, opts?.token);
export const getLiveSeaterAvailability = (opts: any) => reportGet('/live/checkin/seater-wise-availability', opts, opts?.token);
export const getLiveGuestCount = (opts: any) => reportGet('/live/checkin/inqueue-guest-count', opts, opts?.token);
export const getLiveCheckInStatus = (opts: any) => reportGet('/live/checkin/status-checkin', opts, opts?.token);
export const getLiveAvgWaitTime = (opts: any) => reportGet('/live/checkin/average-wait-time', opts, opts?.token);
export const getLiveGroupAvgWaitTime = (opts: any) => reportGet('/live/checkin/average-wait-time-by-groups', opts, opts?.token);
export const getLiveCheckInTable = (opts: any) => reportGet('/live/checkin/liveortoday-checkins', { ...opts, type: opts?.type || 'livecheckin' }, opts?.token);
export const getLiveCheckInToday = (opts: any) => reportGet('/live/checkin/liveortoday-checkins', { ...opts, type: 'todaycheckin' }, opts?.token);
// Check-In Overview (historical; requires locationId, startDate, endDate)
export const getCheckInOverviewTotals = (opts: any) => reportGet('/checkin/overview/totals', opts, opts?.token);
export const getCheckInOverviewHourly = (opts: any) => reportGet('/checkin/overview/hourlyCheckin', opts, opts?.token);
export const getCheckInOverviewGuestsHourly = (opts: any) => reportGet('/checkin/overview/hourlyGuests', opts, opts?.token);
export const getCheckInOverviewDailyAndGuest = (opts: any) => reportGet('/checkin/overview/daily-checkIns-and-guests', opts, opts?.token);
export const getCheckInOverviewDineInGroup = (opts: any) => reportGet('/checkin/overview/dineInDuration', opts, opts?.token);
export const getCheckInOverviewGuestSize = (opts: any) => reportGet('/checkin/overview/guestSizeDistribution', opts, opts?.token);
export const getCheckInOverviewTableDetails = (opts: any) => reportGet('/checkin/overview/checkin-details', opts, opts?.token);
export const getCheckInOverviewTopCustomer = (opts: any) => reportGet('/checkin/overview/top-repeat-customers', opts, opts?.token);
export const getCheckInOverviewAvgWaitTimeGroup = (opts: any) => reportGet('/checkin/overview/avgWaitTimeByGroup', opts, opts?.token);

// --- Reservation (same as maghil-portal reservationReportsApi) ---
export const getTodaysReservationOverview = (opts: any) => reportGet('/reservations/today-reservation/overview', opts, opts?.token);
export const getTodaysReservationVolumeBySession = (opts: any) => reportGet('/reservations/todays-reservation/volume-by-session', opts, opts?.token);
export const getTodaysReservationSeated = (opts: any) => reportGet('/reservations/today-reservation/seated-details', opts, opts?.token);
export const getTodaysReservationAssigned = (opts: any) => reportGet('/reservations/today-reservation/assigned-details', opts, opts?.token);
export const getUpcomingReservationOverview = (opts: any) => reportGet('/reservations/upcoming-reservation/overview', opts, opts?.token).then(res => { console.log('[ReservationReport] getUpcomingReservationOverview (/reservations/upcoming-reservation/overview):', JSON.stringify(res, null, 2)); return res; });
export const getUpcomingReservationVolumeBySession = (opts: any) => reportGet('/reservations/upcoming-reservation/volume-by-session', opts, opts?.token);
export const getUpcomingReservationDetails = (opts: any) => reportGet('/reservations/upcoming-reservation/details', opts, opts?.token);
export const getReservationOverviewTotals = (opts: any) => reportGet('/reservations/overview-reservations/totals', opts, opts?.token).then(res => { console.log('[ReservationReport] getReservationOverviewTotals (/reservations/overview-reservations/totals):', JSON.stringify(res, null, 2)); return res; });
export const getReservationOverviewSlotWise = (opts: any) => reportGet('/reservations/overview-reservations/overview/slot-wise-reservation', opts, opts?.token);
export const getReservationOverviewDailyAndGuests = (opts: any) => reportGet('/reservations/overview-reservations/daily-reservation-and-guests', opts, opts?.token).then(res => { console.log('[getReservationOverviewDailyAndGuests] response:', res); return res; });
export const getReservationOverviewDetails = (opts: any) => reportGet('/reservations/overview-reservations/overview-details', opts, opts?.token);

// --- Reservation Analytics (new endpoints) ---
export const getReservationTurnTimeAccuracy = (opts: any) => reportGet('/reservations/analytics/turn-time-accuracy', opts, opts?.token).catch(() => []);
export const getReservationChannelDistribution = (opts: any) => reportGet('/reservations/analytics/channel-distribution', opts, opts?.token).catch(() => []);
export const getReservationHourlyCovers = (opts: any) => reportGet('/reservations/analytics/hourly-covers', opts, opts?.token).catch(() => []);
export const getReservationHeatmap = (opts: any) => reportGet('/reservations/analytics/heatmap', opts, opts?.token).catch(() => []);
export const getReservationNoShowSummary = (opts: any) => reportGet('/reservations/analytics/no-show-summary', opts, opts?.token).then(res => { console.log('[ReservationReport] getReservationNoShowSummary (/reservations/analytics/no-show-summary):', JSON.stringify(res, null, 2)); return res; }).catch(() => ({}));
export const getReservationAvgWaitTime = (opts: any) => reportGet('/reservations/analytics/avg-wait-time', opts, opts?.token).catch(() => ({}));
export const getReservationSeatingEfficiency = (opts: any) => reportGet('/reservations/analytics/seating-efficiency', opts, opts?.token).catch(() => ({}));
export const getReservationPartySizeDelta = (opts: any) => reportGet('/reservations/analytics/party-size-delta', opts, opts?.token).catch(() => ({}));

// --- Customer (same as maghil-portal customerInsightsApi) ---
export const getCustomerVolumeSummary = (opts: any) => reportGet('/customer/insights/customer/volume-summary', opts, opts?.token);
export const getCustomerByTenure = (opts: any) => reportGet('/customer/insights/customerByTenure', opts, opts?.token);
export const getCustomerTotalSpend = (opts: any) => reportGet('/customer/insights/customer/total-spend', opts, opts?.token);
export const getCustomerAvgCoverSize = (opts: any) => reportGet('/customer/insights/avgCoverSize', opts, opts?.token);
export const getCustomerLoyaltyCounts = (opts: any) => reportGet('/customer/insights/customer/loyalty-counts', opts, opts?.token);
export const getCustomerInfo = (opts: any) => reportGet('/customer/insights/customer-info', opts, opts?.token);
export const getCustomerInsightsSummary = (opts: any) => reportGet('/customer/insights/summary', opts, opts?.token);
export const getCustomerDineIn = (opts: any) => reportGet('/customer/insights/dinein-customer', opts, opts?.token);
export const getCustomerOnline = (opts: any) => reportGet('/customer/insights/online-customer', opts, opts?.token);
export const getCustomerOrderHistory = (opts: any) => reportGet('/customer/insights/customerOrderHistory', opts, opts?.token);
export const getCustomerLatestOrder = (opts: any) => reportGet('/customer/insights/latest-order-items', opts, opts?.token);
export const getCustomerFavouriteItems = (opts: any) => reportGet('/customer/insights/favourite-items', opts, opts?.token);

// --- Event (same as maghil-portal eventReportsApi) ---
export const getEventOverview = (opts: any) => reportGet('/events/event-overview', opts, opts?.token);
export const getEventPreOrderByOrderDate = (opts: any) => reportGet('/events/pre-order/order-date', opts, opts?.token);
export const getEventPreOrderByPickup = (opts: any) => reportGet('/events/pre-order/pickup', opts, opts?.token);
export const getEventPreOrderChannels = (opts: any) => reportGet('/events/pre-order/channels', opts, opts?.token);
export const getEventPreOrderDetails = (opts: any) => reportGet('/events/pre-order/details', opts, opts?.token);
export const getEventPreOrderSlots = (opts: any) => reportGet('/events/pre-order/slots', opts, opts?.token);
export const getEventNameFilter = (opts: any) => reportGet('/events/event-name', opts, opts?.token);
export const getEventItemSales = (opts: any) => reportGet('/events/item-sales', opts, opts?.token);

// --- Loyalty ---
// GET /sales/loyalty/loyalty-summary?locationId=&startDate=&endDate=
export const getLoyaltySummary = (opts: any) => reportGet('/sales/loyalty/loyalty-summary', opts, opts?.token);
// GET /sales/loyalty/points-flow-trend?locationId=&startDate=&endDate=
export const getLoyaltyPointsFlowTrend = (opts: any) => reportGet('/sales/loyalty/points-flow-trend', opts, opts?.token);
// GET /sales/loyalty/members-growth?locationId=&startDate=&endDate=
export const getLoyaltyMembersGrowth = (opts: any) => reportGet('/sales/loyalty/members-growth', opts, opts?.token);
// GET /sales/loyalty/top-members?locationId=&sortBy=&sortOrder=&page=&size=
export const getLoyaltyTopMembers = (opts: any) => reportGet('/sales/loyalty/top-members', opts, opts?.token);
// GET /sales/loyalty/transaction-breakdown?locationId=&startDate=&endDate=
export const getLoyaltyTransactionBreakdown = (opts: any) => reportGet('/sales/loyalty/transaction-breakdown', opts, opts?.token);
// GET /sales/loyalty/activity-log?locationId=&startDate=&endDate=&page=&size=
export const getLoyaltyActivityLog = (opts: any) => reportGet('/sales/loyalty/activity-log', opts, opts?.token);
// GET /sales/loyalty/attributed-revenue?locationId=&startDate=&endDate=
export const getLoyaltyAttributedRevenue = (opts: any) => reportGet('/sales/loyalty/attributed-revenue', opts, opts?.token);
// GET /sales/loyalty/aov-comparison?locationId=&startDate=&endDate=
export const getLoyaltyAovComparison = (opts: any) => reportGet('/sales/loyalty/aov-comparison', opts, opts?.token);
// GET /sales/loyalty/member-profile?loyaltyCustomerId=&dollarValuePerPoint=
export const getLoyaltyMemberProfile = (opts: any) => reportGet('/sales/loyalty/member-profile', opts, opts?.token);
// GET /sales/loyalty/discount-performance?locationId=&startDate=&endDate=&source=&orderSource=
// Drives the Discount & Promotion Performance card in the Discounts drill-down.
// `source` (PROMOTIONAL | LOYALTY | MANUAL) is optional — omitted so one call returns
// every segment; the source pills filter the returned segments client-side.
// Not routed through reportGet/buildQuery: it needs `source` / `orderSource`.
export const getLoyaltyDiscountPerformance = ({ locationId, startDate, endDate, source, orderSource, token }: any) => {
    const params = new URLSearchParams();
    if (locationId) params.set('locationId', locationId);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (source) params.set('source', source);
    if (orderSource) params.set('orderSource', orderSource);
    return fetchReport(`${REPORTS_BASE_URL}/sales/loyalty/discount-performance?${params}`, token);
};
// GET /sales/loyalty/discount-log?locationId=&startDate=&endDate=&source=&orderSource=&search=&sortBy=&sortOrder=&page=&size=
// Order-level rows behind the Discount & Promotion Performance card. Backend
// paginated and 1-based: `page=1` is the first page.
export const getLoyaltyDiscountLog = ({ locationId, startDate, endDate, source, orderSource, search, sortBy, sortOrder, page, size, token }: any) => {
    const params = new URLSearchParams();
    if (locationId) params.set('locationId', locationId);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (source) params.set('source', source);
    if (orderSource) params.set('orderSource', orderSource);
    if (search) params.set('search', search);
    if (sortBy) params.set('sortBy', sortBy);
    if (sortOrder) params.set('sortOrder', sortOrder);
    if (page != null) params.set('page', String(page));
    if (size != null) params.set('size', String(size));
    return fetchReport(`${REPORTS_BASE_URL}/sales/loyalty/discount-log?${params}`, token);
};

// --- Unpaid Orders (Snapshot SNAP-14/15) ---
// GET /sales/unpaid-orders/summary?locationId=&startDate=&endDate=
export const getUnpaidOrdersSummary = (opts: any) =>
    reportGet('/sales/unpaid-orders/summary', opts, opts?.token).catch(() => ({ totalCount: 0, totalAmount: 0, avgAmount: 0, serverBreakdown: [] }));
// GET /sales/unpaid-orders?locationId=&startDate=&endDate=&page=&size=&sortBy=&sortAsc=
export const getUnpaidOrdersLog = (opts: any) =>
    reportGet('/sales/unpaid-orders', opts, opts?.token).catch(() => ({ content: [], totalElements: 0, totalPages: 0 }));

// --- Order Log (Sales Report SALES-7/8/9) ---
// GET /sales/order-log?locationId=&startDate=&endDate=&page=&size=&sortBy=&sortAsc=&search=&orderType=&minAmount=&maxAmount=&customerName=&minGuests=
export const getOrderLog = (opts: any) =>
    reportGet('/sales/order-log', opts, opts?.token).catch(() => ({ content: [], totalElements: 0, totalPages: 0 }));
// GET /sales/order-detail?locationId=&orderId=
export const getOrderDetail = (opts: any) =>
    reportGet('/sales/order-detail', opts, opts?.token).catch(() => null);

// --- Owner View: Sales Overview by Days (same as merchant_portal / hotfix) ---
// GET /sales/getSalesOverviewByDays?locationId=uuid,uuid,...&startDate=&endDate=&page=&size=&sortBy=&sortAsc=&considerThirdPartyValues=
export const getSalesOverviewByDays = (opts: any) => {
    const params = new URLSearchParams();
    const locId = opts?.locationId ?? opts?.locationid ?? '';
    if (locId) params.set('locationId', locId);
    if (opts?.startDate) params.set('startDate', opts.startDate);
    if (opts?.endDate) params.set('endDate', opts.endDate);
    params.set('page', String(opts?.page ?? 0));
    params.set('size', String(opts?.size ?? 10));
    if (opts?.sortBy) params.set('sortBy', opts.sortBy);
    if (opts?.sortAsc != null) params.set('sortAsc', opts.sortAsc);
    if (opts?.considerThirdPartyValues != null) params.set('considerThirdPartyValues', opts.considerThirdPartyValues);
    return fetchReport(`${VOICE_AGENT_BASE_URL}/sales/getSalesOverviewByDays?${params}`, opts?.token, 'Bearer');
};

// Date helpers â€” use local date so the 1st of the month stays the 1st (toISOString would give previous day in UTC for timezones ahead of UTC)
export const toDateStr = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};
// Anchor "today"/"yesterday" on the store's timezone (`timeZoneCd`), not the
// browser's — a store in Chicago viewed from an IST browser must resolve the
// Chicago date. When no zone is passed, fall back to the active store timezone
// (set from BranchContext), then to browser-local. Existing callers that pass no
// argument therefore become store-tz-correct automatically.
export const todayStr = (timeZoneCd: string = getStoreTimeZone()): string => dateStrInZone(new Date(), timeZoneCd);
export const yesterdayStr = (timeZoneCd: string = getStoreTimeZone()): string => shiftDateStr(todayStr(timeZoneCd), -1);

// ZAR-1762 — Decide "is this today?" from the RESOLVED range, not from the filter label.
// The ClickHouse live endpoints (/sales/live/v2/*) return today's rows only when
// startDate/endDate are omitted; passing today→today comes back empty. Label-based checks
// (`activeDate === 'Today'`) miss the date picker, which emits `custom:<today>:<today>` for
// the same business day — so picking today from the calendar sent the dates and the panel
// read as "no data". Comparing the range keeps the picker and the Today pill in agreement.
export const isTodayRange = (startDate?: string, endDate?: string, today: string = todayStr()): boolean =>
    !!startDate && startDate === today && (endDate ?? startDate) === today;

/**
 * Convert a date-filter token to { startDate, endDate }. Returns null only for a token
 * no preset recognises.
 *
 * THE single implementation (GLOBAL-DP / ZAR-1639): the logic lives in
 * `utils/reportPeriod.resolveTokenRange`, and the copies that used to sit in
 * SnapshotScreen, SalesScreen and useEodSummaryData now delegate here. That matters for
 * the shared cross-report filter — a range chosen on EOD ("Last 3 Months") used to
 * resolve to null on Snapshot and silently collapse to a single day.
 *
 * Historical presets are anchored on the store-timezone **anchor date** — the last
 * finalised day under the 5 AM cutoff (see getAnchorDate / DateProdIssue.md) — NOT the
 * raw `yesterday` argument, which has no cutoff. `today` is still honoured for the
 * 'Today' branch so callers that pass their own already-computed day keep that exact
 * value; `timeZoneCd` defaults to the active store timezone.
 */
export const getStartEndFromFilter = (
    filter: string,
    today: string,
    yesterday: string,
    timeZoneCd: string = getStoreTimeZone()
): { startDate: string; endDate: string } | null => {
    if (normalizeToken(filter) === 'today') return { startDate: today, endDate: today };
    return resolveTokenRange(filter, timeZoneCd);
};

/**
 * Human-readable label for a date filter — the "Date range" line in an "All
 * outlets" export file header, and any on-screen "· <period>" caption.
 * Presets keep their UI wording ("Last 7 Days"); a custom range resolves to the
 * actual dates ("28 July 2026 to 30 July 2026"). Mirrors the local copies in
 * SnapshotScreen / SalesScreen so every Reports export reads the same.
 */
export const getFilterLabel = (filter?: string | null, startDate?: string | null, endDate?: string | null): string => {
    if (!filter) return 'Today';
    if (filter === 'Today') return 'Today';
    if (filter === 'Yesterday') return 'Yesterday';
    if (filter === '7D') return 'Last 7 Days';
    if (filter === '30D') return 'Last 30 Days';
    if (filter === 'This Month') return 'This Month';
    const fmt = (d: string) => {
        const dt = new Date(`${d}T12:00:00`);
        return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    };
    // The date picker emits `custom:<start>:<end>`; some callers pass a bare
    // 'Custom' alongside the resolved range instead.
    if (filter.startsWith('custom:')) {
        const p = filter.split(':');
        if (p.length === 3) return p[1] === p[2] ? fmt(p[1]) : `${fmt(p[1])} to ${fmt(p[2])}`;
    }
    if (filter === 'Custom' && startDate && endDate) {
        return startDate === endDate ? fmt(startDate) : `${fmt(startDate)} to ${fmt(endDate)}`;
    }
    return filter;
};
